<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import DataTable from 'primevue/datatable';
import type {
  DataTablePageEvent,
  DataTableSortEvent,
  DataTableRowExpandEvent,
} from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import type { InputNumberInputEvent } from 'primevue/inputnumber';
import Card from 'primevue/card';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import {
  isPlatformAdmin,
  getAssessmentTypeName,
  type SortOrder,
  type UserSortField,
  type IUser,
  type IAssessmentWithDetails,
  type IHesAssessmentConfiguration,
} from '@manage/shared-types';
import { formatDate } from '@/utils/formatters';
import {
  DATATABLE_ROWS_PER_PAGE_OPTIONS,
  DATATABLE_PAGINATOR_TEMPLATE,
  DATATABLE_CURRENT_PAGE_REPORT_TEMPLATE,
} from '@/constants/dataTable';
import AccommodationSettingsModal from '@/components/AccommodationSettingsModal.vue';
import ResetAssessmentsModal from '@/components/ResetAssessmentsModal.vue';
import { apiClient } from '@/services/api';
import { useAssessmentsStore } from '../stores/assessments';
import { useUsersStore, MIN_TEXT_FILTER_LENGTH } from '../stores/users';
import { useAuthStore } from '../stores/auth';

// AchieveWorks Aptitudes configuration ID
const ACHIEVEWORKS_APTITUDES_CONFIG_ID = 2000;

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const usersStore = useUsersStore();
const assessmentsStore = useAssessmentsStore();
const toast = useToast();

// Handle access denied redirect feedback
const accessDeniedMessage = ref<string | null>(null);

if (route.query.accessDenied) {
  accessDeniedMessage.value = `You don't have permission to access ${route.query.accessDenied}`;
  // Clear the query param from URL without triggering navigation
  router.replace({ path: route.path, query: {} });
}

const expandedRows = ref<IUser[]>([]);

// HES Assessment modal state
const showHesAssessment = ref(false);
const currentAssessmentUser = ref<IUser | null>(null);
const currentAssessment = ref<IAssessmentWithDetails | null>(null);

// Reset assessments modal state
const showResetModal = ref(false);
const resetParentAssessmentId = ref<number | null>(null);
const resetUserId = ref<number | null>(null);

// Simple reset confirmation state (non-AWA assessments)
const showSimpleResetConfirm = ref(false);
const simpleResetAssessmentId = ref<number | null>(null);
const simpleResetUserId = ref<number | null>(null);
const simpleResetLoading = ref(false);

// Accommodation Settings modal state
const showAccommodationSettings = ref(false);
const accommodationAssessment = ref<IAssessmentWithDetails | null>(null);
const accommodationUserName = ref<string | null>(null);

const isPlatformAdminFlag = computed(() =>
  authStore.user ? isPlatformAdmin(authStore.user) : false
);

const isFiltersDefault = computed(() => {
  return (
    (usersStore.filters.id === null || usersStore.filters.id === undefined) &&
    usersStore.filters.name === '' &&
    usersStore.filters.email === '' &&
    usersStore.filters.accountName === '' &&
    (usersStore.filters.accountId === null || usersStore.filters.accountId === undefined)
  );
});

const DEBOUNCE_TIME_MS = 700;
let debounceTimer: number | undefined;

/**
 * Check if a text filter is valid for API call.
 * Valid means: empty OR has MIN_TEXT_FILTER_LENGTH+ characters.
 * Filters with 1-2 chars are "in progress" and should not trigger API calls.
 */
const isTextFilterValid = (value: string): boolean => {
  const trimmed = value.trim();

  return trimmed.length === 0 || trimmed.length >= MIN_TEXT_FILTER_LENGTH;
};

/**
 * Check if all text filters are valid for API call.
 * Prevents flickering by not calling API when user is mid-typing (1-2 chars).
 */
const areTextFiltersValid = (): boolean => {
  return (
    isTextFilterValid(usersStore.filters.name) &&
    isTextFilterValid(usersStore.filters.email) &&
    isTextFilterValid(usersStore.filters.accountName)
  );
};

/**
 * Handle text filter update from PrimeVue InputText.
 * Using @update:modelValue for proper two-way binding.
 */
const handleTextFilterUpdate = (
  field: 'name' | 'email' | 'accountName',
  value: string | undefined
) => {
  usersStore.filters[field] = value ?? '';
};

/**
 * Clear whitespace-only text filters after debounce.
 * Store update triggers InputText to sync via modelValue binding.
 */
const clearWhitespaceOnlyFilters = () => {
  const fields: Array<'name' | 'email' | 'accountName'> = ['name', 'email', 'accountName'];

  for (const field of fields) {
    const value = usersStore.filters[field];

    if (value && !value.trim()) {
      usersStore.filters[field] = '';
    }
  }
};

const debouncedFetchUsers = () => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer);
  }

  // Don't trigger API call if text filters are mid-typing (1-2 chars)
  if (!areTextFiltersValid()) {
    return;
  }

  debounceTimer = window.setTimeout(() => {
    clearWhitespaceOnlyFilters();
    void usersStore.fetchUsers();
  }, DEBOUNCE_TIME_MS);
};

const clearAllFilters = () => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer);
  }

  usersStore.clearAllFilters();
  void usersStore.fetchUsers();
};

const parseNumericInput = (event: InputNumberInputEvent): number | null => {
  const rawValue = event.value;

  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();

    if (trimmed === '') {
      return null;
    }

    const parsed = Number.parseInt(trimmed, 10);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const onIdInput = (event: InputNumberInputEvent) => {
  usersStore.filters.id = parseNumericInput(event);
};

const onAccountIdInput = (event: InputNumberInputEvent) => {
  usersStore.filters.accountId = parseNumericInput(event);
};

const onPage = (event: DataTablePageEvent) => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer);
  }

  expandedRows.value = []; // Collapse all rows when paging

  const nextPage = ((event.page ?? 0) as number) + 1;
  const nextPageSize = (event.rows ?? usersStore.pageSize) as number;

  usersStore.setPage(nextPage);
  usersStore.setPageSize(nextPageSize);
  void usersStore.fetchUsers();
};

const onSort = (event: DataTableSortEvent) => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer);
  }

  expandedRows.value = []; // Collapse all rows when sorting

  const nextSortField = typeof event.sortField === 'string' ? event.sortField : undefined;
  const nextSortOrder =
    event.sortOrder === 1 || event.sortOrder === -1 ? (event.sortOrder as SortOrder) : undefined;

  usersStore.setSort(nextSortField as UserSortField | undefined, nextSortOrder);
  void usersStore.fetchUsers();
};

const onRowExpand = async (event: DataTableRowExpandEvent) => {
  const user = event.data as IUser;

  try {
    await assessmentsStore.fetchUserAssessments(user.id);
  } catch (error) {
    console.error('Failed to load assessments for user:', user.id, error);
  }
};

const hasCompletedStatus = (assessment: IAssessmentWithDetails): boolean => {
  const status = assessment.status?.toLowerCase();

  return status === 'report' || status === 'parentreport';
};

const isAchieveWorksAptitudes = (assessment: IAssessmentWithDetails): boolean => {
  return assessment.type === 'awAptitudes';
};

const openAccommodationSettings = (user: IUser, assessment: IAssessmentWithDetails) => {
  accommodationAssessment.value = assessment;
  accommodationUserName.value = `${user.firstName} ${user.lastName}`;
  showAccommodationSettings.value = true;
};

const closeAccommodationSettings = () => {
  showAccommodationSettings.value = false;
  accommodationAssessment.value = null;
  accommodationUserName.value = null;
};

const openResetModal = (assessment: IAssessmentWithDetails, userId: number) => {
  if (assessment.type === 'awAptitudes') {
    resetParentAssessmentId.value = assessment.id;
    resetUserId.value = userId;
    showResetModal.value = true;
  } else {
    simpleResetAssessmentId.value = assessment.id;
    simpleResetUserId.value = userId;
    showSimpleResetConfirm.value = true;
  }
};

const handleSimpleReset = async () => {
  if (!simpleResetAssessmentId.value) {
    return;
  }

  simpleResetLoading.value = true;

  try {
    const assessmentId = Number(simpleResetAssessmentId.value);
    const result = await assessmentsStore.resetChildAssessments(assessmentId, [assessmentId]);

    toast.add({
      severity: 'success',
      summary: 'Assessment Reset',
      detail: result.message || 'Successfully reset assessment',
      life: 5000,
    });

    if (simpleResetUserId.value) {
      try {
        await assessmentsStore.fetchUserAssessments(simpleResetUserId.value);
      } catch {
        toast.add({
          severity: 'warn',
          summary: 'List Out of Date',
          detail:
            'Reset succeeded, but the assessment list could not be refreshed. Please reload the page.',
          life: 8000,
        });
      }
    }
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Reset Failed',
      detail: assessmentsStore.resetModalError || 'Failed to reset assessment',
      life: 5000,
    });
  } finally {
    simpleResetLoading.value = false;
    showSimpleResetConfirm.value = false;
    simpleResetAssessmentId.value = null;
    simpleResetUserId.value = null;
  }
};

const handleResetComplete = async () => {
  showResetModal.value = false;

  if (resetUserId.value) {
    try {
      await assessmentsStore.fetchUserAssessments(resetUserId.value);
    } catch {
      toast.add({
        severity: 'warn',
        summary: 'List Out of Date',
        detail:
          'Reset succeeded, but the assessment list could not be refreshed. Please reload the page.',
        life: 8000,
      });
    }
  }

  resetParentAssessmentId.value = null;
  resetUserId.value = null;
};

const getFilteredAssessments = (userId: number) => {
  // Filter out child assessments (those with a parent)
  // Only show parent assessments in the UI
  return assessmentsStore.getAssessments(userId).filter((a) => a.parentAssessmentId === null);
};

const importSpa = async (spaUrl: string) => {
  try {
    await import(/* @vite-ignore */ spaUrl);
  } catch (err) {
    throw new Error('Failed to load assessment', { cause: err });
  }
};

const viewAssessmentReport = async (user: IUser, assessment: IAssessmentWithDetails) => {
  try {
    // Show the modal and set current user/assessment
    currentAssessmentUser.value = user;
    currentAssessment.value = assessment;
    showHesAssessment.value = true;

    // Wait for Vue to render the modal DOM
    await nextTick();

    // Get HES SPA token for this user
    const response = await apiClient.get(`/users/${user.id}/spa-token`);
    const { token } = response.data;

    // Determine API environment from env vars
    const apiEnvironment = import.meta.env.VITE_HES_API_ENV;

    if (!apiEnvironment) {
      throw new Error('VITE_HES_API_ENV is not configured');
    }

    const enableDataDog = import.meta.env.VITE_HES_ENABLE_DATADOG === 'true';

    // Get HES client URL from environment
    const spaUrl =
      import.meta.env.VITE_HES_SPA_URL ||
      'https://pkg.humanesources.com/js/@humanesources.com/assessment-client@pre2/dist/assessment-client.es.js';
    const clientBaseUrl = spaUrl.replace('/assessment-client.es.js', '');

    await importSpa(spaUrl);

    // Check if the HES client is available
    if (!window.startHesAssessmentClient) {
      throw new Error('HES Assessment Client not loaded');
    }

    const configuration: IHesAssessmentConfiguration = {
      assessmentId: assessment.id.toString(),
      enableDataDog,
      clientBaseUrl,
      apiEnvironment,
      configurationId: String(ACHIEVEWORKS_APTITUDES_CONFIG_ID),
      userId: user.id.toString(),
      locales: [],
      applicationDivId: 'hes-assessment-client',
      apiToken: token,
      displayBadging: true,
      readOnly: true,
    };

    // Start the HES assessment client
    window.startHesAssessmentClient(configuration);
  } catch (error) {
    console.error('Failed to launch SPA:', error);
    showHesAssessment.value = false;
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to launch assessment viewer. Please try again later.',
      life: 5000,
    });
  }
};

const closeHesAssessment = () => {
  showHesAssessment.value = false;
  currentAssessmentUser.value = null;
  currentAssessment.value = null;

  // Clear the HES assessment container
  const container = document.getElementById('hes-assessment-client');

  if (container) {
    container.innerHTML = '';
  }

  // Clean up Accommodation Settings modal if open when navigating away
  if (showAccommodationSettings.value) {
    closeAccommodationSettings();
  }
};

const stopWatch = watch(
  () => usersStore.filters,
  () => {
    expandedRows.value = []; // Collapse all rows when filters change
    usersStore.setPage(1);
    debouncedFetchUsers();
  },
  { deep: true }
);

onMounted(async () => {
  if (authStore.isAuthenticated) {
    await usersStore.fetchUsers();
  }
});

onBeforeUnmount(() => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer);
  }

  stopWatch(); // Stop watching to prevent timer from firing after unmount

  // Clean up HES assessment SPA if open when navigating away
  if (showHesAssessment.value) {
    closeHesAssessment();
  }

  // Clean up Accommodation Settings modal if open when navigating away
  if (showAccommodationSettings.value) {
    closeAccommodationSettings();
  }
});
</script>

<template>
  <div class="content-container">
    <Message
      v-if="accessDeniedMessage"
      severity="warn"
      icon="pi pi-exclamation-triangle"
      :closable="true"
      @close="accessDeniedMessage = null"
    >
      {{ accessDeniedMessage }}
    </Message>

    <div class="main-heading-container users-toolbar">
      <h1>
        <i class="pi pi-users"></i>
        Users
      </h1>
      <Button
        label="Clear All Filters"
        icon="pi pi-filter-slash"
        outlined
        severity="secondary"
        :disabled="isFiltersDefault"
        @click="clearAllFilters"
      />
    </div>

    <Message v-if="usersStore.error" severity="error" icon="pi pi-exclamation-triangle">
      {{ usersStore.error }}
    </Message>

    <div v-if="usersStore.filterPromptVisible" class="limited-results-container">
      <Message severity="info" icon="pi pi-filter" :closable="false" data-cy="filter-prompt-banner">
        Apply filters to search users.
      </Message>
    </div>

    <DataTable
      v-model:expandedRows="expandedRows"
      data-cy="users-table"
      :value="usersStore.users"
      :loading="usersStore.loading"
      lazy
      stripedRows
      paginator
      :rows="usersStore.pageSize"
      :first="usersStore.firstRowIndex"
      :totalRecords="usersStore.total"
      :rowsPerPageOptions="DATATABLE_ROWS_PER_PAGE_OPTIONS"
      :paginatorTemplate="DATATABLE_PAGINATOR_TEMPLATE"
      :currentPageReportTemplate="DATATABLE_CURRENT_PAGE_REPORT_TEMPLATE"
      sortMode="single"
      :sortField="usersStore.sortField"
      :sortOrder="usersStore.sortOrder"
      removableSort
      filterDisplay="row"
      responsiveLayout="scroll"
      class="users-table p-datatable-sm"
      @page="onPage"
      @sort="onSort"
      @row-expand="onRowExpand"
    >
      <Column expander style="width: 50px" />
      <Column
        field="id"
        sortable
        header="ID"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        style="width: 110px"
      >
        <template #filter>
          <div class="filter-input">
            <InputNumber
              v-model="usersStore.filters.id"
              data-cy="filter-id"
              :useGrouping="false"
              placeholder="ID"
              inputClass="filter-number"
              @input="onIdInput"
            />
            <Button
              v-if="usersStore.filters.id !== null"
              rounded
              variant="text"
              icon="pi pi-times"
              size="small"
              title="Clear ID filter"
              aria-label="Clear ID filter"
              @click="usersStore.filters.id = null"
            />
          </div>
        </template>
      </Column>

      <Column
        field="firstName"
        sortable
        header="Name"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        style="width: 260px"
        bodyClass="wrap-text"
      >
        <template #body="{ data }">
          <div class="user-name">{{ data.firstName }} {{ data.lastName }}</div>
        </template>
        <template #filter>
          <div class="filter-input">
            <InputText
              :modelValue="usersStore.filters.name"
              data-cy="filter-name"
              placeholder="Filter by name (min 3)"
              class="filter-text"
              @update:model-value="(val) => handleTextFilterUpdate('name', val)"
            />
            <Button
              v-if="usersStore.filters.name"
              rounded
              variant="text"
              icon="pi pi-times"
              size="small"
              title="Clear name filter"
              aria-label="Clear name filter"
              @click="usersStore.filters.name = ''"
            />
          </div>
        </template>
      </Column>

      <Column
        field="email"
        sortable
        header="Email"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        style="width: 320px"
      >
        <template #filter>
          <div class="filter-input">
            <InputText
              :modelValue="usersStore.filters.email"
              data-cy="filter-email"
              placeholder="Filter by email (min 3)"
              class="filter-text"
              @update:model-value="(val) => handleTextFilterUpdate('email', val)"
            />
            <Button
              v-if="usersStore.filters.email"
              rounded
              variant="text"
              icon="pi pi-times"
              size="small"
              title="Clear email filter"
              aria-label="Clear email filter"
              @click="usersStore.filters.email = ''"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="isPlatformAdminFlag"
        field="accountName"
        sortable
        header="Account Name"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        style="width: 240px"
        bodyClass="wrap-text"
      >
        <template #filter>
          <div class="filter-input">
            <InputText
              :modelValue="usersStore.filters.accountName"
              data-cy="filter-account-name"
              placeholder="Filter by account name (min 3)"
              class="filter-text"
              @update:model-value="(val) => handleTextFilterUpdate('accountName', val)"
            />
            <Button
              v-if="usersStore.filters.accountName"
              rounded
              variant="text"
              icon="pi pi-times"
              size="small"
              title="Clear account name filter"
              aria-label="Clear account name filter"
              @click="usersStore.filters.accountName = ''"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="isPlatformAdminFlag"
        field="accountId"
        sortable
        header="Account ID"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        style="width: 160px"
      >
        <template #filter>
          <div class="filter-input">
            <InputNumber
              v-model="usersStore.filters.accountId"
              data-cy="filter-account-id"
              :useGrouping="false"
              placeholder="Filter by account"
              inputClass="filter-number"
              @input="onAccountIdInput"
            />
            <Button
              v-if="usersStore.filters.accountId !== null"
              rounded
              variant="text"
              icon="pi pi-times"
              size="small"
              title="Clear account filter"
              @click="usersStore.filters.accountId = null"
            />
          </div>
        </template>
      </Column>

      <Column field="lastLogin" sortable header="Last Login" style="width: 160px">
        <template #body="{ data }">
          {{ data.lastLogin ? new Date(data.lastLogin).toLocaleDateString() : 'Never' }}
        </template>
      </Column>

      <Column field="createdAt" sortable header="Created At" style="width: 160px">
        <template #body="{ data }">
          {{ data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'N/A' }}
        </template>
      </Column>

      <template #expansion="{ data }">
        <div class="assessment-expansion" :data-cy="`user-${data.id}-assessments`">
          <h3 class="expansion-title">Assessments for {{ data.firstName }} {{ data.lastName }}</h3>

          <div
            v-if="assessmentsStore.isLoading(data.id)"
            class="loading-container"
            data-cy="assessments-loading"
          >
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
            <span>Loading assessments...</span>
          </div>

          <Message
            v-else-if="assessmentsStore.getError(data.id)"
            severity="error"
            icon="pi pi-exclamation-triangle"
            data-cy="assessments-error"
          >
            {{ assessmentsStore.getError(data.id) }}
          </Message>

          <div v-else-if="getFilteredAssessments(data.id).length === 0" data-cy="assessments-empty">
            <Message severity="info" icon="pi pi-info-circle">
              No assessments found for this user.
            </Message>
          </div>

          <div v-else class="assessments-container" data-cy="assessments-container">
            <Card
              v-for="assessment in getFilteredAssessments(data.id)"
              :key="assessment.id"
              class="assessment-card"
              :data-cy="`assessment-card-${assessment.id}`"
            >
              <template #title>
                <div class="assessment-card-header">
                  {{ assessment.productName || getAssessmentTypeName(assessment.type) }}
                </div>
              </template>
              <template #content>
                <div class="assessment-details">
                  <div class="detail-row">
                    <span class="detail-label">Created:</span>
                    <span class="detail-value">{{ formatDate(assessment.createdAt) }}</span>
                  </div>
                  <div v-if="assessment.completedAt" class="detail-row">
                    <span class="detail-label">Completed:</span>
                    <span class="detail-value">{{ formatDate(assessment.completedAt) }}</span>
                  </div>
                </div>
              </template>
              <template v-if="hasCompletedStatus(assessment)" #footer>
                <div v-if="hasCompletedStatus(assessment)" class="action-buttons">
                  <Button
                    v-if="hasCompletedStatus(assessment)"
                    label="View Report"
                    icon="pi pi-file"
                    size="small"
                    :data-cy="`view-report-btn-${assessment.id}`"
                    @click="viewAssessmentReport(data, assessment)"
                  />
                  <Button
                    v-if="isAchieveWorksAptitudes(assessment)"
                    label="Accommodation Settings"
                    icon="pi pi-wrench"
                    size="small"
                    :data-cy="`accommodation-settings-btn-${assessment.id}`"
                    @click="openAccommodationSettings(data, assessment)"
                  />
                  <Button
                    v-if="hasCompletedStatus(assessment)"
                    label="Reset Assessments"
                    icon="pi pi-undo"
                    size="small"
                    severity="danger"
                    :data-cy="`reset-btn-${assessment.id}`"
                    @click="openResetModal(assessment, data.id)"
                  />
                </div>
              </template>
            </Card>
          </div>
        </div>
      </template>

      <template #empty>
        <Message
          v-if="!usersStore.filterPromptVisible"
          severity="secondary"
          icon="pi pi-exclamation-circle"
        >
          No users found.
        </Message>
      </template>
    </DataTable>

    <!-- HES Assessment Modal -->
    <Dialog
      v-model:visible="showHesAssessment"
      :modal="true"
      :closable="true"
      :dismissableMask="false"
      header="Assessment Report"
      :style="{ width: '95vw', height: '95vh' }"
      data-cy="hes-assessment-dialog"
      @hide="closeHesAssessment"
    >
      <div id="hes-assessment-client" data-cy="hes-assessment-client"></div>
    </Dialog>

    <!-- Reset Assessments Modal -->
    <ResetAssessmentsModal
      v-model:visible="showResetModal"
      :parentAssessmentId="resetParentAssessmentId"
      :userId="resetUserId"
      @reset-complete="handleResetComplete"
    />

    <!-- Simple Reset Confirmation (non-AWA assessments) -->
    <Dialog
      v-model:visible="showSimpleResetConfirm"
      :modal="true"
      :closable="!simpleResetLoading"
      header="Reset Assessment"
      :style="{ width: '450px', maxWidth: '95vw' }"
      data-cy="simple-reset-dialog"
    >
      <p>
        Are you sure you want to reset the assessment? This person's results will be permanently
        deleted, and they will have to complete the assessment again.
      </p>
      <template #footer>
        <div class="modal-footer">
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            :disabled="simpleResetLoading"
            data-cy="simple-reset-cancel-btn"
            @click="showSimpleResetConfirm = false"
          />
          <Button
            label="Reset"
            severity="danger"
            :loading="simpleResetLoading"
            :disabled="simpleResetLoading"
            data-cy="simple-reset-confirm-btn"
            @click="handleSimpleReset"
          />
        </div>
      </template>
    </Dialog>
    <!-- Accommodation Settings Modal -->
    <AccommodationSettingsModal
      v-model:visible="showAccommodationSettings"
      :parentAssessmentId="accommodationAssessment?.id ? Number(accommodationAssessment.id) : null"
      :userName="accommodationUserName"
      @save-complete="closeAccommodationSettings"
    />
  </div>
</template>

<style lang="scss" scoped>
.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .limited-results-container {
    min-height: 50px; // Reserve space to prevent layout shift
  }

  .main-heading-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;

    h1 {
      margin: 0;
      padding-bottom: 0;
    }
  }

  .filter-input {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-text {
    width: 100%;
  }

  .users-table {
    border: 4px solid white;
    @include rounded-corners($sm-radius);
    @include drop-shadow(0, 0, 3px, 1px, rgba(0, 0, 0, 0.1));

    :deep(.p-datatable-table) {
      table-layout: fixed;
    }

    :deep(th),
    :deep(td) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-name {
      font-size: 1.05rem;
    }

    .user-id {
      font-size: 0.85rem;
      color: var(--p-text-muted-color);
    }
  }

  :deep(.filter-number) {
    width: 100%;
  }

  .assessment-expansion {
    max-width: calc(100vw - 92px);
    padding: 20px;

    .expansion-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--p-text-color);
    }

    .loading-container {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 20px;
      color: var(--p-text-muted-color);
    }

    .assessments-container {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      gap: 16px;

      .assessment-card {
        border: 3px solid var(--p-primary-100);

        :deep(.p-card-body) {
          padding: 0;
        }

        :deep(.p-card-title) {
          padding: 1rem 1rem 0 1rem;
          font-size: 1rem;

          .assessment-card-header {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 8px;

            > span:first-child {
              font-weight: 600;
              line-height: 1.3;
            }
          }
        }

        :deep(.p-card-content) {
          padding: 0 1rem;

          .assessment-details {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .detail-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 4px 0;
              border-bottom: 1px solid var(--p-slate-200);

              .detail-label {
                font-size: 0.875rem;
                font-weight: 600;
                color: var(--p-text-muted-color);
              }

              .detail-value {
                font-size: 0.875rem;
                color: var(--p-text-color);
              }
            }
          }
        }

        :deep(.p-card-footer) {
          padding: 0.5rem 1rem 1rem 1rem;
          margin: 0;

          .action-buttons {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
        }
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
